import {
  ForwardedRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import {
  useGetIntroducerApplication,
  useGetSignupState,
} from 'lendingApi/lendingApiHooks';
import { FormState, Resolver, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { TImperativeHandleSignup } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useRouterApplicationId } from '../hooks/useRouterApplicationId';
import { useSignupData } from '../hooks/useSignupData';
import { useSignupStep } from '../hooks/useSignupStep';
import { TRefinancingDebt } from '../RefinancingDebts/refinancing.types';
import { useShowRefinancing } from '../RefinancingDebts/useShowRefinancing.hook';
import { schema } from './SignUpForm.schema';
import { TSignupFormData } from './SignupForm.types';
import {
  clearHiddenRefinancing,
  getFrontendValidationErrors,
  parseBackendErrorMessage,
} from './SignUpFormHelper';

type TUseSignupFormParams = {
  ref: ForwardedRef<TImperativeHandleSignup>;
  setSaveForLaterLoading?: (data: boolean) => void;
};

export const useSignUpForm = ({
  ref,
  setSaveForLaterLoading,
}: TUseSignupFormParams) => {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { updateLatestAllowedStep } = useSignupStep();
  const {
    stateKey,
    applicationData,
    stateData,
    updateSignupData,
    isReintroduction,
  } = useSignupData();
  const { checkIntroducerPermission } = useIntroducerData();
  const isDisabled = !checkIntroducerPermission('can_create_applications');
  const applicationId = useRouterApplicationId();
  const { state, stateStatus } = useGetSignupState();
  const { application, applicationStatus } = useGetIntroducerApplication();
  const stateHasLoaded = state && stateStatus === 'success';
  const applicationHasLoaded = application && applicationStatus === 'success';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const defaultValues = {
    // Company values
    companyName: stateData.companyName,
    companyNumber: stateData.companyNumber,
    companyType: stateData.companyType,
    tradingName: stateData.tradingName,
    website: stateData.website,
    startDateMonth: stateData.startDateMonth,
    startDateYear: stateData.startDateYear,
    last12MonthsTurnover: stateData.last12MonthsTurnover,
    isVatRegistered: stateData.isVatRegistered,
    // Client values
    applicant: stateData.applicant,
    guarantors: stateData.guarantors,
    // Loan application values
    amount: isReintroduction ? undefined : applicationData.amount,
    loanDuration: applicationData.loanDuration,
    loanPurpose: applicationData.loanPurpose,
    loanPurposeDetails: applicationData.loanPurposeDetails,
    // Reuse the saved debts if the application already has some; otherwise
    // start with a single empty debt so the section shows one blank card.
    refinancing: applicationData.refinancing ?? {
      debts: [{ facilityType: '' }] as TRefinancingDebt[],
    },
  };

  const showRefinancingRef = useRef(false);
  const resolver = useMemo<Resolver<TSignupFormData>>(
    () => (values, context, options) =>
      yupResolver(schema(isReintroduction, showRefinancingRef.current))(
        values,
        context,
        options,
      ),
    [isReintroduction],
  );

  const methods = useForm<TSignupFormData>({
    resolver,
    defaultValues: defaultValues,
  });

  const [amount, loanPurpose] = useWatch({
    control: methods.control,
    name: ['amount', 'loanPurpose'],
  });
  const showRefinancing = useShowRefinancing({ amount, loanPurpose });
  showRefinancingRef.current = showRefinancing;

  // Clear refinancing errors when the section hides — the fields read straight
  // from formState, so without this old errors would flash back up if the
  // section is re-shown (e.g. the amount dips below threshold and back). Also
  // clear the submit banner, which otherwise stays up after a refinancing-only
  // validation failure even though the offending fields are gone.
  useEffect(() => {
    if (!showRefinancing) {
      methods.clearErrors('refinancing');
      setSignupError(null);
    }
  }, [showRefinancing, methods]);

  useEffect(() => {
    if (applicationHasLoaded && stateHasLoaded) {
      methods.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationHasLoaded, stateHasLoaded]);

  // React hook that allows this referenced component to return a single function instead of the entire return value of the element
  useImperativeHandle(
    ref,
    () => {
      return {
        async saveFormData() {
          setSaveForLaterLoading?.(true);
          await updateSignupData({
            data: clearHiddenRefinancing(
              methods.getValues(),
              showRefinancingRef.current,
            ),
            isSaveAndFinishLater: true,
          });
          navigate(PATH_NAMES.home());
          setSaveForLaterLoading?.(false);
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, updateSignupData],
  );

  const onSubmit = async (values: TSignupFormData) => {
    setIsSubmitting(true);
    setSignupError(null);

    try {
      if (!stateKey) {
        return;
      }

      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['offers'],
      });

      trackEvent(AMPLITUDE_EVENTS.newApplicationSubmitted, {
        'State key': stateKey,
        Reintroduction: isReintroduction,
      });

      const { updatedState } = await updateSignupData({
        data: clearHiddenRefinancing(values, showRefinancingRef.current),
      });

      await updateLatestAllowedStep('new/productOffers', updatedState);
      navigate(PATH_NAMES.newApplicationProductOffers({ applicationId }));
    } catch (e: unknown) {
      const errorMessage = parseBackendErrorMessage(e);
      setSignupError(errorMessage);
      trackError(errorMessage, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const trackError = (error?: string, isBackendError = false) => {
    if (!error) return;

    trackEvent(AMPLITUDE_EVENTS.newApplicationSubmissionErrored, {
      'Error message': error,
      Reintroduction: isReintroduction,
      'Backend error': isBackendError,
    });

    datadogLogs.logger.error('Failed to submit application', {
      createError: error,
      stateKey,
      isReintroduction,
      isBackendError,
    });
  };

  // Handle Frontend Validation Error
  const onError = (errors: FormState<TSignupFormData>['errors']) => {
    if (!errors) return;

    setSignupError(
      'Some fields are missing or invalid. Please check and try again.',
    );
    getFrontendValidationErrors(errors).forEach((error) => trackError(error));
  };

  return {
    isDisabled,
    signupError,
    onSubmit,
    onError,
    isSubmitting,
    methods,
    stateHasLoaded,
    applicationHasLoaded,
    showRefinancing,
  };
};
